<script setup lang="ts">
import { ref, watch } from 'vue';
import InputFieldGroup from './InputFieldGroup.vue';
import RequiredLabel from './RequiredLabel.vue';
import ErrorSlot from './ErrorSlot.vue';

const props = defineProps<{
  label: string;
  modelValue: number;
  step?: number;
  min?: number;
  max?: number;
  errorText?: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

function format(value: number): string {
  if (Number.isNaN(value)) return '';
  const rounded = Math.round(value * 10) / 10;
  return String(rounded).replace('.', ',');
}

const inputValue = ref<string>(format(props.modelValue));
const cleared = ref<boolean>(false);
let lastSynced = inputValue.value;

watch(
  () => props.modelValue,
  (value) => {
    cleared.value = false;
    if (inputValue.value === '') return;
    const formatted = format(value);
    if (formatted !== lastSynced && formatted !== inputValue.value) {
      inputValue.value = formatted;
      lastSynced = formatted;
    } else if (formatted === inputValue.value) {
      lastSynced = formatted;
    }
  },
);

function onInput(e: Event): void {
  const target = e.target as HTMLInputElement;
  const cleaned = target.value.replace(/[^0-9.,]/g, '').replace('.', ',');
  target.value = cleaned;
  lastSynced = cleaned;
  if (cleaned === '') {
    cleared.value = true;
    return;
  }
  cleared.value = false;
  const parsed = Number(cleaned.replace(',', '.'));
  if (!Number.isNaN(parsed)) {
    emit('update:modelValue', parsed);
  }
}

function stepValue(value: number): number {
  const step = props.step ?? 0.1;
  const decimals = Math.round(-Math.log10(step));
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function clamp(value: number): number {
  const min = props.min ?? 0;
  const max = props.max ?? 100;
  return Math.min(max, Math.max(min, value));
}

function decrement(): void {
  const step = props.step ?? 0.1;
  const base = cleared.value ? 0 : props.modelValue;
  const next = clamp(stepValue(base - step));
  inputValue.value = format(next);
  lastSynced = inputValue.value;
  cleared.value = false;
  emit('update:modelValue', next);
}

function increment(): void {
  const step = props.step ?? 0.1;
  const base = cleared.value ? 0 : props.modelValue;
  const next = clamp(stepValue(base + step));
  inputValue.value = format(next);
  lastSynced = inputValue.value;
  cleared.value = false;
  emit('update:modelValue', next);
}
</script>

<template>
  <div class="block" data-testid="input-percentage">
    <RequiredLabel :text="label" :has-error="false" />
    <InputFieldGroup
      :has-error="false"
      stepper="plusminus"
      @increment="increment"
      @decrement="decrement"
    >
      <input
        v-model="inputValue"
        type="text"
        inputmode="decimal"
        autocomplete="off"
        class="flex-1 min-w-0 h-10 px-2.5 bg-transparent outline-none text-body-medium text-text-primary"
        @input="onInput"
      />
      <span class="px-2.5 text-label-small text-text-tertiary">% ročně</span>
    </InputFieldGroup>
    <ErrorSlot :text="errorText ?? null" />
  </div>
</template>
